import { useEffect, type PropsWithChildren } from "react";
import { AuthProvider, type AuthProviderProps } from "react-oidc-context";
import { WebStorageStateStore } from "oidc-client-ts";
import type { OidcRuntimeConfig } from "./config";

type OidcAuthProviderProps = PropsWithChildren<{
  config: OidcRuntimeConfig;
}>;

function cleanupCallbackUrl(): void {
  const callbackPath =
    window.location.pathname === "/auth/callback" ? "/" : window.location.pathname;
  window.history.replaceState({}, document.title, callbackPath);
}

function isLocalhostEnvironment(): boolean {
  const { hostname } = window.location;
  return hostname === "localhost" || hostname === "127.0.0.1";
}

export function OidcAuthProvider({ config, children }: OidcAuthProviderProps) {
  const isLocalhost = isLocalhostEnvironment();
  const isProxyUrl = isLocalhost && new URLSearchParams(window.location.search).get("proxy") === "true";

  useEffect(() => {
    if (isProxyUrl) {
      window.sessionStorage.setItem("useProxy", "true");
    }
  }, [isProxyUrl]);

  const useProxy = isLocalhost && (isProxyUrl || window.sessionStorage.getItem("useProxy") === "true");

  const metadata = useProxy ? {
    issuer: config.authority,
    authorization_endpoint: `${config.authority}/authorize`,
    token_endpoint: `${window.location.origin}/proxy/idp/token`,
    userinfo_endpoint: `${window.location.origin}/proxy/idp/userinfo`,
    end_session_endpoint: `${config.authority}/logout`,
    jwks_uri: `${window.location.origin}/proxy/idp/.well-known/jwks.json`,
  } : undefined;

  const oidcConfig: AuthProviderProps = {
    authority: config.authority,
    metadata,
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    post_logout_redirect_uri: config.postLogoutRedirectUri,
    response_type: "code",
    disablePKCE: true,
    scope: config.scope,
    loadUserInfo: false,
    automaticSilentRenew: false,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
    onSigninCallback: cleanupCallbackUrl,
    matchSignoutCallback: (settings) => {
      const logoutRedirectUri =
        settings.post_logout_redirect_uri ?? config.postLogoutRedirectUri;
      return Boolean(
        logoutRedirectUri && window.location.href.startsWith(logoutRedirectUri)
      );
    },
    onSignoutCallback: () => {
      window.history.replaceState({}, document.title, "/");
    },
    onRemoveUser: () => {
      window.history.replaceState({}, document.title, "/");
    }
  };

  return <AuthProvider {...oidcConfig}>{children}</AuthProvider>;
}
